import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { fetchClubPosts } from "../../services/clubsApi";
import { clubFeedColumnShellClass } from "./feedPostCardPresentation";
import { useAuth } from "../../context/AuthContext";
import FeedPostCard, { pickViewerId } from "./FeedPostCard";

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

function isActiveMember(membership) {
  if (!membership) return false;
  const s = String(membership.status || "").toLowerCase();
  return s === "active";
}

const ClubFeedPanel = forwardRef(function ClubFeedPanel(
  {
    club,
    membership,
    accent = "emerald",
    className = "",
    feedReloadToken = 0,
    clubSlug: clubSlugProp,
  },
  ref
) {
  const { user } = useAuth();
  const viewerId = pickViewerId(user);
  const navigate = useNavigate();

  const clubSlug = useMemo(
    () =>
      String(clubSlugProp || club?.slug || "")
        .trim(),
    [clubSlugProp, club?.slug]
  );

  const clubId = useMemo(
    () => club?.club_id ?? club?.id ?? null,
    [club?.club_id, club?.id]
  );

  const canParticipate = isActiveMember(membership);

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [shareFeedback, setShareFeedback] = useState(null);

  const loadPage = useCallback(
    async (offset, append) => {
      if (!clubId) return;
      const limit = 15;
      const rows = await fetchClubPosts(clubId, { limit, offset });
      const list = Array.isArray(rows) ? rows : [];
      setHasMore(list.length >= limit);
      setPosts((prev) => (append ? prev.concat(list) : list));
    },
    [clubId]
  );

  useEffect(() => {
    if (!clubId) {
      setLoading(false);
      setPosts([]);
      return;
    }

    let alive = true;
    setErr("");
    setLoading(true);
    setHasMore(true);

    loadPage(0, false)
      .catch((e) => alive && setErr(e?.message || "Failed to load posts"))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [clubId, loadPage]);

  const refreshPosts = useCallback(async () => {
    if (!clubId) return;
    setErr("");
    setLoading(true);
    setHasMore(true);
    try {
      await loadPage(0, false);
    } catch (e) {
      setErr(e?.message || "Failed to load posts.");
    } finally {
      setLoading(false);
    }
  }, [clubId, loadPage]);

  const feedScrollRef = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      refreshPosts,
      getFeedScrollElement: () => feedScrollRef.current,
    }),
    [refreshPosts]
  );

  useEffect(() => {
    if (!clubId || !feedReloadToken) return;
    refreshPosts();
  }, [clubId, feedReloadToken, refreshPosts]);

  const onLoadMore = async () => {
    if (!clubId || loadingMore || !hasMore) return;
    try {
      setLoadingMore(true);
      await loadPage(posts.length, true);
    } catch (e) {
      setErr(e?.message || "Failed to load more.");
    } finally {
      setLoadingMore(false);
    }
  };

  const goToThread = useCallback(
    (post, navState = undefined) => {
      if (!post?.id || !clubSlug) return;
      navigate(`/clubs/${encodeURIComponent(clubSlug)}/posts/${post.id}`, {
        state: { ...(navState && typeof navState === "object" ? navState : {}), threadOrigin: "club" },
      });
    },
    [navigate, clubSlug]
  );

  const syncPostInList = useCallback((postId, updates, feedRowSource) => {
    const matchSrc = feedRowSource || "club_post";
    setPosts((prev) =>
      prev.map((x) => {
        const rowSrc = x.feed_row_source || "club_post";
        return x.id === postId && rowSrc === matchSrc ? { ...x, ...updates } : x;
      })
    );
  }, []);

  if (!clubId) {
    return (
      <div
        className={cn(
          "rounded-2xl border border-white/12 bg-white/[0.02] p-6 text-white/60 text-sm",
          className
        )}
      >
        Club feed is unavailable (missing club id).
      </div>
    );
  }

  return (
    <div
      className={cn(
        clubFeedColumnShellClass,
        "flex flex-col gap-6 min-h-0",
        className
      )}
    >
      {err ? (
        <div className="shrink-0 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {err}
        </div>
      ) : null}

      <div
        ref={feedScrollRef}
        className={cn(
          "flex min-h-0 w-full flex-col gap-6 min-w-0",
          "scrollbar-hide md:flex-1 md:overflow-y-auto md:overscroll-y-contain"
        )}
      >
        {loading ? (
          <div className="flex items-center justify-center py-16 text-white/50 gap-2 text-sm shrink-0">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading posts…
          </div>
        ) : posts.length === 0 ? (
          <div className="shrink-0 rounded-2xl border border-white/12 bg-white/[0.02] p-8 text-center">
            <div className="text-white font-semibold">No posts yet</div>
            <p className="mt-2 text-sm text-white/55 max-w-md mx-auto">
              {canParticipate
                ? "Start the feed with the + button — publish a post when you have something to share."
                : "When members publish posts, they will appear here."}
            </p>
          </div>
        ) : (
          <div className="space-y-5 sm:space-y-6">
            {posts.map((p) => (
              <FeedPostCard
                key={p.feed_row_source ? `${p.feed_row_source}:${p.id}` : p.id}
                post={p}
                viewerId={viewerId}
                accent={accent}
                clubSlug={clubSlug}
                club={club}
                membership={membership}
                canInteract={canParticipate}
                shareFeedbackId={shareFeedback?.postId}
                shareFeedbackText={shareFeedback?.text}
                onLikeToggled={(postId, liked, likeCount, feedRowSource) => {
                  syncPostInList(postId, { liked_by_viewer: liked, like_count: likeCount }, feedRowSource);
                }}
                onDeleted={(postId, feedRowSource) => {
                  const matchSrc = feedRowSource || "club_post";
                  setPosts((prev) =>
                    prev.filter((x) => {
                      const rowSrc = x.feed_row_source || "club_post";
                      return !(x.id === postId && rowSrc === matchSrc);
                    })
                  );
                }}
                onOpenThread={goToThread}
                onShareFeedback={(postId, text) => {
                  setShareFeedback({ postId, text });
                  window.setTimeout(() => setShareFeedback(null), 2200);
                }}
              />
            ))}
          </div>
        )}

        {hasMore && posts.length > 0 ? (
          <div className="flex justify-center pt-3 sm:pt-4 shrink-0">
            <button
              type="button"
              onClick={onLoadMore}
              disabled={loadingMore}
              className="h-11 px-6 rounded-xl border border-white/14 bg-white/[0.03] text-white/85 hover:border-white/22 hover:bg-white/[0.05] disabled:opacity-50 text-sm transition"
            >
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
});

export default ClubFeedPanel;
